import React, { useState, useEffect, useRef, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTheme } from '@emotion/react';
import { darkTheme, lightTheme } from '../themes';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/utils/formatters';
import useResponsiveChartHeight from '@/components/highcharts/powerAnalyser/hooks/useResponsiveChartHeight';

const ChildAnalysisChart = ({
  data,
  setSeriesColors,
  isLoading,
  dataSeriesAggregate,
}) => {
  const chartComponentRef = useRef(null);
  const theme = useTheme();
  const [chartOptions, setChartOptions] = useState({});

  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const chartHeight = useResponsiveChartHeight();
  const getFilterLabel = (type, value) => {
    switch (type) {
      case 'activeHoursFilter':
        switch (value) {
          case true:
            return t('filter_labels.active_hours');
          case false:
            return t('filter_labels.inactive_hours');
          default:
            return t('filter_labels.all_hours');
        }
      case 'activeDaysFilter':
        switch (value) {
          case true:
            return t('filter_labels.active_days');
          case false:
            return t('filter_labels.inactive_days');
          default:
            return t('filter_labels.all_days');
        }
      case 'activeHolidaysFilter':
        switch (value) {
          case true:
            return t('filter_labels.holidays_included');
          case false:
            return t('filter_labels.holidays_excluded');
          default:
            return '';
        }
      default:
        return '';
    }
  };

  const generateLegendName = (series) => {
    if (!series || !series.metadata) return '';
    const { activeDaysFilter, activeHoursFilter, activeHolidaysFilter } =
      series.metadata || {};

    const parts = [];

    parts.push(getFilterLabel('activeDaysFilter', activeDaysFilter));

    parts.push(getFilterLabel('activeHoursFilter', activeHoursFilter));

    const holidayLabel = getFilterLabel(
      'activeHolidaysFilter',
      activeHolidaysFilter,
    );
    if (holidayLabel) parts.push(holidayLabel);

    return parts.length > 0 ? parts.join(', ') : '';
  };

  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoading) {
        chart.showLoading('Loading data...');
      } else {
        chart.hideLoading();
      }
    }
  }, [isLoading]);

  // Build categories and chart series only when `data` changes
  const { allChildNames, chartSeries } = useMemo(() => {
    if (!data?.length) return { allChildNames: [], chartSeries: [] };

    const allChildNames = Array.from(
      new Set(
        data.flatMap((series) =>
          series.ChildNodesSummaries?.map((child) => child.Name),
        ),
      ),
    );

    const chartSeries = data.map((series) => {
      const childNameToValue = Object.fromEntries(
        series.ChildNodesSummaries.map((child) => [
          child.Name,
          child?.[dataSeriesAggregate?.value] ?? 0,
        ]),
      );
      const orderedData = allChildNames.map(
        (name) => childNameToValue[name] ?? 0,
      );
      return {
        name: generateLegendName(series),
        data: orderedData,
      };
    });

    return { allChildNames, chartSeries };
  }, [data, dataSeriesAggregate, t]);

  // Set chart options based on processed data
  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;
    setChartOptions({
      chart: {
        type: 'column',
        height: chartHeight,
        spacingLeft: 40, // ✅ Adds left padding
        spacingRight: 40, // ✅ Adds right padding
        backgroundColor: themeOptions.chart.backgroundColor,
      },
      title: { text: '' },
      xAxis: {
        categories: allChildNames,
        gridLineColor: 'transparent',
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        lineColor: themeOptions.xAxis.lineColor,
        labels: { style: { color: themeOptions.xAxis.labels.style.color } },
      },
      yAxis: {
        title: { text: `${t('common.energy_usage')} (kWh)` },
        gridLineColor: 'transparent',
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        min: 0,
        labels: { style: { color: themeOptions.yAxis.labels.style.color } },
      },
      tooltip: {
        shared: true,
        formatter: function () {
          let tooltip = `<b>${this.x}</b><br/>`;
          this.points.forEach((point) => {
            tooltip += `<span style="color:${point.color}">\u25CF</span> ${
              point.series.name
            }: <b>${formatNumber(point.y)} kWh</b><br/>`;
          });
          return tooltip;
        },
        backgroundColor: themeOptions.tooltip.backgroundColor,
        borderColor: themeOptions.tooltip.borderColor,
        style: {
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
      },
      plotOptions: {
        column: {
          grouping: true,
          borderWidth: 0,
        },
      },
      legend: {
        itemStyle: { color: themeOptions.legend.itemStyle.color },
      },
      series: chartSeries,
      credits: { enabled: false },
    });
  }, [allChildNames, chartSeries]);

  // Theme update effect
  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      chart.update(
        {
          chart: { backgroundColor: themeOptions.chart.backgroundColor },
          time: { useUTC: true },
          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
            labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          },
          yAxis: [
            {
              gridLineColor: 'transparent',
              labels: {
                style: { color: themeOptions.yAxis.labels.style.color },
              },
            },
          ],
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          tooltip: {
            backgroundColor: themeOptions.tooltip.backgroundColor,
            borderColor: themeOptions.tooltip.borderColor,
            style: {
              color: theme.palette.mode === 'dark' ? 'white' : 'black',
            },
          },
        },
        false,
      );
      chart.redraw();
    }
  }, [theme]);

  // Extract colors for parent
  useEffect(() => {
    if (!setSeriesColors || typeof setSeriesColors !== 'function') return;
    const chart = chartComponentRef.current?.chart;
    if (!chart) return;
    const colorMap = chart.series.map((s) => ({
      name: s.name,
      color: s.color,
    }));
    setSeriesColors(colorMap);
  }, [chartOptions, setSeriesColors]);

  if (!data?.length) return <div>No data</div>;

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={chartOptions}
      ref={chartComponentRef}
    />
  );
};

export default ChildAnalysisChart;
